import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import Button from "./ui/Button";
import { type ComplianceStatus, submitCompliance } from "../api/compliance";
import { getErrorMessage } from "../utils/errors";

interface ComplianceModalProps {
    outdatedCompliances: ComplianceStatus[];
    onAllAccepted: () => void;
    onError: (message: string) => void;
}

export default function ComplianceModal({
    outdatedCompliances,
    onAllAccepted,
    onError,
}: ComplianceModalProps): React.JSX.Element {
    const [accepted, setAccepted] = useState<Record<string, boolean>>({});
    const [submitting, setSubmitting] = useState(false);

    const toggleAccepted = (complianceId: string) => {
        setAccepted((prev) => ({ ...prev, [complianceId]: !prev[complianceId] }));
    };

    const allChecked = outdatedCompliances.every((c) => accepted[c.compliance_id]);

    const handleAccept = async () => {
        try {
            setSubmitting(true);
            await Promise.all(outdatedCompliances.map((c) => submitCompliance(c.compliance_id)));
            onAllAccepted();
        } catch (error_) {
            console.error("Failed to submit consents:", error_);
            onError(getErrorMessage(error_, "Failed to submit consents. Please try again."));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
                {/* Backdrop */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="absolute inset-0"
                    style={{ background: 'var(--c-charcoal-60)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}
                    aria-hidden="true"
                />

                {/* Modal Card */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: 10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 10 }}
                    transition={{ type: "spring", stiffness: 350, damping: 25 }}
                    className="relative w-full max-w-lg overflow-hidden"
                    style={{
                        background: 'var(--c-cream)',
                        borderRadius: 'var(--r-xl)',
                        boxShadow: 'var(--sh-lifted)',
                        border: '1px solid var(--rule)',
                    }}
                >
                    {/* Header */}
                    <div className="p-8 pb-6" style={{ borderBottom: '1px solid var(--rule)' }}>
                        <div className="flex items-center gap-3 mb-2">
                            <div
                                className="p-2"
                                style={{ background: 'var(--c-teal-weak)', borderRadius: 'var(--r-md)', color: 'var(--c-kivo-teal)' }}
                            >
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="stroke-current stroke-[1.5]">
                                    <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5.586a1 1 0 0 1 .707.293l5.414 5.414a1 1 0 0 1 .293.707V19a2 2 0 0 1-2 2Z" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                            <h2 style={{ font: `600 var(--fs-h3)/1.2 var(--f-text)`, color: 'var(--ink)', margin: 0 }}>
                                Legal Updates
                            </h2>
                        </div>
                        <p style={{ fontSize: 'var(--fs-body)', lineHeight: 1.6, color: 'var(--ink-muted)', margin: 0 }}>
                            We&apos;ve improved our policies to give you more control and clarity.
                            Please review and accept the changes to continue using{' '}
                            <span style={{ fontWeight: 600, color: 'var(--ink)' }}>kivo.skin</span>.
                        </p>
                    </div>

                    {/* Scrollable Content */}
                    <div
                        className="p-6 overflow-y-auto space-y-4"
                        style={{ maxHeight: '50vh', background: 'var(--c-charcoal-06)' }}
                    >
                        {outdatedCompliances.map((compliance) => {
                            const isChecked = accepted[compliance.compliance_id] ?? false;
                            return (
                                <label
                                    key={compliance.compliance_id}
                                    className="group relative flex items-start gap-4 p-4 cursor-pointer select-none"
                                    style={{
                                        background: isChecked ? 'var(--c-teal-weak)' : 'var(--c-cream-weak)',
                                        border: `1px solid ${isChecked ? 'rgba(42,164,166,0.3)' : 'var(--rule)'}`,
                                        borderRadius: 'var(--r-md)',
                                        transition: `border-color var(--t-fast) var(--ease), background var(--t-fast) var(--ease)`,
                                    }}
                                >
                                    {/* Custom Checkbox */}
                                    <div className="relative flex-shrink-0 mt-1">
                                        <input
                                            type="checkbox"
                                            checked={isChecked}
                                            onChange={() => { toggleAccepted(compliance.compliance_id); }}
                                            className="peer sr-only"
                                        />
                                        <div
                                            className="w-5 h-5 flex items-center justify-center"
                                            style={{
                                                borderRadius: 'var(--r-xs)',
                                                border: `1px solid ${isChecked ? 'var(--c-kivo-teal)' : 'var(--rule)'}`,
                                                background: isChecked ? 'var(--c-kivo-teal)' : 'var(--c-cream-weak)',
                                                color: isChecked ? 'var(--c-cream)' : 'transparent',
                                                transition: `background var(--t-fast) var(--ease), border-color var(--t-fast) var(--ease)`,
                                            }}
                                        >
                                            <svg className="w-3.5 h-3.5" viewBox="0 0 12 10" fill="none">
                                                <path d="M1 5l3 3 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </div>
                                    </div>

                                    <div className="flex-1">
                                        <div className="flex items-center justify-between gap-2">
                                            <span
                                                className="font-semibold"
                                                style={{
                                                    fontSize: 'var(--fs-body)',
                                                    color: isChecked ? 'var(--c-kivo-teal)' : 'var(--ink)',
                                                    transition: `color var(--t-fast) var(--ease)`,
                                                }}
                                            >
                                                {compliance.display_name}
                                            </span>
                                            <span
                                                className="uppercase font-bold tracking-wider"
                                                style={{
                                                    fontSize: 10,
                                                    color: 'var(--ink-muted)',
                                                    background: 'var(--c-charcoal-06)',
                                                    padding: '2px 6px',
                                                    borderRadius: 'var(--r-xs)',
                                                }}
                                            >
                                                v{compliance.current_version}
                                            </span>
                                        </div>

                                        <div className="mt-1" style={{ fontSize: 'var(--fs-small)', color: 'var(--ink-muted)' }}>
                                            Tap to view the full details of this document.
                                        </div>

                                        <a
                                            href={compliance.content_url.replace("/v1/compliance/content/", "/")}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={(e) => { e.stopPropagation(); }}
                                            className="inline-flex items-center gap-1 mt-3 py-1 underline underline-offset-2"
                                            style={{ fontSize: 'var(--fs-small)', fontWeight: 500, color: 'var(--ink-muted)' }}
                                        >
                                            Read full policy
                                            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6m4-3h6m0 0v6m0-6L10 14" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </a>
                                    </div>
                                </label>
                            );
                        })}
                    </div>

                    {/* Footer */}
                    <div
                        className="p-6 flex flex-col items-center"
                        style={{ borderTop: '1px solid var(--rule)', background: 'var(--c-cream)' }}
                    >
                        <Button
                            onClick={handleAccept}
                            disabled={!allChecked || submitting}
                            className={`w-full justify-center text-center ${!allChecked ? 'grayscale' : ''}`}
                        >
                            {submitting ? (
                                <span className="flex items-center justify-center gap-2">
                                    <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                    </svg>
                                    Processing...
                                </span>
                            ) : "Accept & Continue"}
                        </Button>
                        <p
                            className="mt-4 text-center"
                            style={{ maxWidth: 360, fontSize: 'var(--fs-label)', lineHeight: 1.5, color: 'var(--ink-muted)' }}
                        >
                            By continuing, you confirm that you have read and agreed to the updated policies.
                        </p>
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
}
